import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// Hooks
import useFilters from '../../../../../../hooks/useFilters';
import { useConfigContext } from '../../../../../Config/hooks/useConfig';
import {
  usePublishDocumentAsync,
  usePublishDocumentAsyncWithStatusCheck,
} from '../../../../hooks/useAsyncPublishDocument';

// Helpers
import { parsePublishRequestData } from '../../../../helpers';
import {
  DOCUMENT_TYPE,
  mapERRORS,
  TAURON_VERIFICATION_CODE_STATUSES,
  mapDOCUMENT_ACTIONS_ERROR,
  RECIPIENT_ACTIONS,
  MAP_CUSTOM_TAURON_ERRORS,
  documentPreparationERRORS,
  UNKNOWN_ERRORS,
} from '../../../../../../constraints';
import {
  RECIPIENT_VALIDATION_PURPOSE,
  RECIPIENT_ERROR_CODES,
} from '../../../../../../constants';
import { PUBLISHING_DOCUMENT_STATUS } from '../../../../constants';
import { QueryKeys } from '../../../../../../queryKeys';

// Components
import * as Styled from './styled';
import { DocumentAtrributesForm } from '../DocumentAtrributesForm';
import { ErrorModal } from '../../../../../../components/ErrorModal';
import { ExportableErrorContent } from '../../../../../../components/ExportableErrorContent';
import { NotificationContentForm } from '../NotificationContentForm/NotificationContentForm';
import { RecipientsFileForm } from '../RecipientsFileForm';
import FileErrorSummary from '../RecipientsFileForm/components/FileErrorSummary';
import { DocumentPublicationSummary } from '../../../DocumentPublicationSummary';
import { PublicatorForm } from '../PublicatorForm';
import { Modal as ModalModule } from 'billon-ui';

const { SuccessModal } = ModalModule;

export const OneStepPublicationAsync = ({
  onCreateIdentity,
  onCreateCategory,
  publicationMode,
  initialStep,
}) => {
  const history = useHistory();
  const { filters, navigateWithNewSearchQuery } = useFilters();
  const { isTauron } = useConfigContext();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(initialStep);
  const [formData, setFormData] = useState({});
  const [documentTitle, setDocumentTitle] = useState(null);

  const documentType = filters.documentTypeList || DOCUMENT_TYPE.PRIVATE;
  const isPrivateDocument = documentType === DOCUMENT_TYPE.PRIVATE;
  const mapErrorBase = isTauron
    ? {
        ...mapERRORS,
        ...TAURON_VERIFICATION_CODE_STATUSES,
        ...MAP_CUSTOM_TAURON_ERRORS,
      }
    : mapERRORS;

  const handleAppendFormData = (moreData) => {
    setFormData({
      ...formData,
      ...moreData,
    });
  };

  const handleSubmitStep = (stepData, isStepObligatory = true) => {
    if (isStepObligatory) {
      handleAppendFormData(stepData);
    }

    if (isPrivateDocument && step === 0) {
      setStep(1);
    } else if (isPrivateDocument && step === 1) {
      setStep(3);
    } else if (
      !isPrivateDocument &&
      step === 2 &&
      stepData?.contactDetails?.length === 0
    ) {
      handleAppendFormData({ contactDetails: [] });
      setStep(4);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = (stepData) => {
    if (step === initialStep) {
      handleReturnDocList();
    } else if (isPrivateDocument && step === 3) {
      setStep(1);
    } else if (
      !isPrivateDocument &&
      step === 4 &&
      (!formData?.contactDetails || formData?.contactDetails?.length === 0)
    ) {
      setStep(2);
    } else {
      if (stepData && step === 1) {
        handleAppendFormData(stepData);
      }
      setStep(step - 1);
    }
  };

  const handleResetRecipientForm = () => {
    setStep(2);
  };

  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const [publishDocumentId, setPublishDocumentId] = useState(null);
  const {
    mutate: publishDocument,
    isLoading,
    reset: resetPublish,
    error: publicationError,
    isError: isPublicationError,
  } = usePublishDocumentAsync(documentType, {
    onSuccess: (response) => {
      const { id } = response;
      setIsLoadingStatus(true);
      setPublishDocumentId(id);
    },
  });

  const {
    data: publishingResult,
    isLoading: isLoadingStatusAsync,
    isRefetching: isRefetchingStatus,
  } = usePublishDocumentAsyncWithStatusCheck(publishDocumentId, {
    enabled: !!publishDocumentId,
    refetchInterval: (data) => {
      if (
        data?.status &&
        [
          PUBLISHING_DOCUMENT_STATUS.SUCCESS,
          PUBLISHING_DOCUMENT_STATUS.PREPARING_ERROR,
        ].includes(data?.status)
      ) {
        setIsLoadingStatus(false);
        return;
      }

      return 2000;
    },
  });

  const reset = () => {
    resetPublish();
    queryClient.invalidateQueries([QueryKeys.PUBLISH_DOCUMENT_ASYNC_STATUS]);
  };

  const handlePublishDocument = (values) => {
    setDocumentTitle(values.title);

    publishDocument({
      data: parsePublishRequestData(
        values,
        publicationMode,
        document?.documentBlockchainAddress,
      ),
    });
  };

  const handleCloseSuccessModal = () => {
    reset();
    navigateWithNewSearchQuery(
      { filters: { documentTypeList: documentType } },
      '/documents',
    );
  };

  const handleReturnDocList = () => {
    reset();
    history.goBack();
  };

  if (
    (!isLoading && publishingResult?.publishingError) ||
    (isPublicationError && publicationError?.statusCode)
  ) {
    let statusCode;
    let errorData;

    if (!isLoading && publishingResult?.publishingError) {
      statusCode = publishingResult?.publishingError;

      if (
        statusCode === documentPreparationERRORS.DOCUMENT_TOO_LARGE &&
        publishingResult?.status
      ) {
        statusCode = `${publishingResult.publishingError}_${publishingResult?.status}`;
      }

      errorData = publishingResult;
    }

    if (isPublicationError && publicationError?.statusCode) {
      statusCode = publicationError?.statusCode || UNKNOWN_ERRORS.UNKNOWN_ERROR;
      errorData = publicationError;
    }

    if (
      [
        publicationError?.statusCode,
        publishingResult?.publishingError,
      ].includes(UNKNOWN_ERRORS.INTERNAL_SERVER_ERROR)
    ) {
      statusCode = UNKNOWN_ERRORS.UNKNOWN_PUBLISHING_ERROR;
    }

    if (
      !isPrivateDocument &&
      statusCode !== UNKNOWN_ERRORS.UNKNOWN_PUBLISHING_ERROR &&
      RECIPIENT_ERROR_CODES[statusCode]
    ) {
      return (
        <>
          <br />
          <FileErrorSummary
            mode={RECIPIENT_ACTIONS.EXTERNAL_VALIDATION}
            errorData={errorData}
            handleResetMutation={handleResetRecipientForm}
            handleExit={handleCloseSuccessModal}
            cancelMessageId="Cancel publication"
            validationPurpose={RECIPIENT_VALIDATION_PURPOSE.PUBLISH}
          />
        </>
      );
    } else {
      const deliveredLabel =
        mapErrorBase[statusCode] ?? mapErrorBase.UNKNOWN_PUBLISHING_ERROR;

      return (
        <ErrorModal
          isOpen
          toggle={handleCloseSuccessModal}
          onClose={handleCloseSuccessModal}
          closeIconEnabled={false}
        >
          <ExportableErrorContent
            headerTitleId={mapDOCUMENT_ACTIONS_ERROR.PUBLISH}
            statusCode={statusCode}
            jobId={document.jobId}
            title={documentTitle}
            deliveredLabel={deliveredLabel}
          />
        </ErrorModal>
      );
    }
  }

  const isPublishingDocument =
    isLoading || isLoadingStatus || isLoadingStatusAsync || isRefetchingStatus;

  return (
    <>
      {step === 0 && (
        <>
          <Styled.BackButton onClick={handleBack}>
            <FormattedMessage id="back" defaultMessage="back" />
          </Styled.BackButton>
          <PublicatorForm
            defaultValues={formData}
            onSubmit={handleSubmitStep}
            submitButtonText="Next"
            isLoading={isLoading}
          />
        </>
      )}
      {step === 1 && (
        <>
          {initialStep === 1 && (
            <Styled.BackButton onClick={handleBack}>
              <FormattedMessage id="back" defaultMessage="back" />
            </Styled.BackButton>
          )}
          <DocumentAtrributesForm
            step={step}
            document={formData}
            publicationMode={publicationMode}
            onSubmit={handleSubmitStep}
            onCreateIdentity={onCreateIdentity}
            onCreateCategory={onCreateCategory}
            submitButtonText="Next"
            isLoading={isPublishingDocument}
            onBack={initialStep === 0 && handleBack}
            initialStep={initialStep}
          />
        </>
      )}
      {step === 2 && (
        <RecipientsFileForm
          onSubmit={handleSubmitStep}
          onBack={handleBack}
          defaultValues={formData}
          validationPurpose={RECIPIENT_VALIDATION_PURPOSE.PUBLISH}
          mode={RECIPIENT_ACTIONS.INSERT_LIST}
          handleCancelProcess={handleCloseSuccessModal}
        />
      )}
      {step === 3 && (
        <NotificationContentForm
          onSubmit={handleSubmitStep}
          onBack={handleBack}
          defaultValues={formData}
          documentType={documentType}
        />
      )}
      {step === 4 && (
        <DocumentPublicationSummary
          onSubmit={handlePublishDocument}
          onBack={handleBack}
          isLoading={isPublishingDocument}
          defaultValues={formData}
          documentType={documentType}
          publicationMode={publicationMode}
        />
      )}
      <SuccessModal
        isOpen={publishingResult?.status === PUBLISHING_DOCUMENT_STATUS.SUCCESS}
        onClose={handleCloseSuccessModal}
      >
        <FormattedMessage
          id="Document publication has been initiated."
          defaultMessage="Document publication has been initiated."
        />
      </SuccessModal>
    </>
  );
};
